import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/auth/authStore";
import { useLanguage } from "@/i18n/LanguageContext";
import { DaaruLogo } from "../branding/DaaruLogo";
import { FingletekCredit } from "../branding/FingletekCredit";
import { LanguageSwitcher } from "./LanguageSwitcher";

export type ShellNavItem = {
  to: string;
  label: string;
  end?: boolean;
};

type Props = {
  title: string;
  subtitle: string;
  homeTo: string;
  items: ShellNavItem[];
  wide?: boolean;
  footerSlot?: ReactNode;
};

export function AuthenticatedShell({ title, subtitle, homeTo, items, wide = false, footerSlot }: Props) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const clear = useAuthStore((s) => s.clear);
  const [open, setOpen] = useState(false);
  const drawerId = useId();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const nav = (
    <nav className="dn-side-nav" aria-label={t.common.mainNav}>
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => (isActive ? "is-active" : undefined)}>
          {item.label}
        </NavLink>
      ))}
      {footerSlot}
      <button
        type="button"
        className="dn-side-nav-logout"
        onClick={() => {
          clear();
          navigate("/");
        }}
      >
        {t.nav.logout}
      </button>
    </nav>
  );

  return (
    <div className={`dn-app-shell${wide ? " dn-app-shell-wide" : ""}`} dir={dir}>
      <a className="dn-skip-link" href="#dn-main-content">
        {t.common.skipToContent}
      </a>
      <header className="dn-app-topbar">
        <button
          type="button"
          className="dn-nav-toggle"
          aria-expanded={open}
          aria-controls={drawerId}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="dn-nav-toggle-bars" aria-hidden="true" />
          <span className="dn-sr-only">{open ? t.common.closeMenu : t.common.openMenu}</span>
        </button>
        <Link to={homeTo} className="dn-brand dn-brand-compact">
          <DaaruLogo size="sm" alt="" aria-hidden="true" />
          <div className="dn-brand-text">
            <strong>{title}</strong>
            <span>{subtitle}</span>
          </div>
        </Link>
        <LanguageSwitcher />
      </header>

      <div className="dn-app-body">
        <aside className="dn-sidebar dn-sidebar-desktop" aria-label={t.common.mainNav}>
          <div className="dn-sidebar-brand">
            <Link to={homeTo} className="dn-brand">
              <DaaruLogo size="md" alt="" aria-hidden="true" />
              <div className="dn-brand-text">
                <strong>{title}</strong>
                <span>{subtitle}</span>
              </div>
            </Link>
          </div>
          {nav}
        </aside>

        <div
          id={drawerId}
          className={`dn-sidebar-drawer${open ? " is-open" : ""}`}
          hidden={!open}
        >
          <div className="dn-sidebar-drawer-panel" role="dialog" aria-modal="true" aria-label={t.common.mainNav}>
            <div className="dn-sidebar-drawer-head">
              <strong>{title}</strong>
              <button type="button" className="dn-btn dn-btn-ghost" onClick={() => setOpen(false)}>
                {t.common.close}
              </button>
            </div>
            {nav}
          </div>
          <button type="button" className="dn-sidebar-backdrop" aria-label={t.common.close} onClick={() => setOpen(false)} />
        </div>

        <main id="dn-main-content" className={`dn-main dn-main-app${wide ? " dn-main-wide" : ""}`}>
          <div key={location.pathname} className="dn-route-fade">
            <Outlet />
          </div>
          <footer className="dn-footer dn-footer-app">
            <FingletekCredit />
          </footer>
        </main>
      </div>
    </div>
  );
}
